import classNames from "classnames";
import { ConditionalLink } from "components/conditionalLink";
import Loader from "components/DesignSystem/Loader/loader";
import Table from "components/DesignSystem/Table/V2/Table";
import { FilingTags } from "components/tasks/taskForForeignCA/FilingTags";
import {
  ChatSlider,
  EstimdatedTimeColumn,
  PreparerColumn,
  TaskNotes,
} from "components/tasks/taskForForeignCA/ForeignCATask";
import { TaskStatus } from "components/TaskStatus/TaskStatus";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useModal } from "hooks/useModal";
import { useQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { useUnreadFromChannels } from "hooks/useUnreadFromChannels";
import { Period } from "pages/Books/FinancialClosing/FinancialClosing";
import { memo, useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  CellContext,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  Row,
  useReactTable,
} from "react-table-8.10.7";
import ArrowBend from "static/images/ArrowBendDownRight.svg";
import Redirect from "static/images/Redirect.svg";
import Search from "static/images/Search.svg";
import VerticalLine from "static/images/VerticalLine.svg";
import VerticalSeparator from "static/images/VerticalSeparator.svg";
import { useGetAllEntityBooksTaskQuery } from "store/apis/bookkeepingTracker";
import { useGetTaskTagsQuery } from "store/apis/task";
import {
  BookkeepingSoftwareType,
  BookkeepingTaskQueryParams,
  ENTITY_BANK_CONNECTION_DETAILS,
  EntityGroup,
  EntityTask,
} from "types/Models/bookkeepingTracker";
import { Task } from "types/Models/task";
import { capitalize } from "utils/capitalize";
import { formatDate } from "utils/formatDate";
import { BookkeepingStartedOn } from "./BookkeepingStartedOn";
import { taskDummyData } from "./DummyDataForLoader";
import { BookkeepingTaskSlider } from "components/BooksCrm/BookkeepingTaskSlider";
import ToolTip from "components/design/toolTip";
import { InfoFilledSmall } from "components/icons/InfoFilledSmall";
import { TaskDeadlineUpdate } from "./TaskDeadlineUpdate";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { BooksGlobalStatus } from "./BooksGlobalStatus";
dayjs.extend(relativeTime);

export const EntityTaskTable = ({
  entityId,
  group,
  bookkeepingSoftware,
  filters,
  connectionDetails,
}: {
  entityId: string;
  group: EntityGroup;
  bookkeepingSoftware: BookkeepingSoftwareType;
  filters: BookkeepingTaskQueryParams;
  connectionDetails: ENTITY_BANK_CONNECTION_DETAILS[];
}) => {
  const {
    data: taskList = [],
    isLoading,
    isSuccess,
    isFetching,
  } = useGetAllEntityBooksTaskQuery(
    {
      entityId,
      ...filters,
    },
    {
      skip: !entityId,
    }
  );

  const [taskId, setTaskId] = useState<string | null>(null);
  const query = useQuery();
  const { data: tags = [] } = useGetTaskTagsQuery();
  const channelUrls: string[] = [];
  const channelUnreadMap = useUnreadFromChannels({
    channelUrls,
    queryOptions: { limit: 30 },
  });
  const taskChatSlider = useModal();
  const selectedTab = query.get("status") || "ALL";
  const [defaultTaskTab, setDefaultTaskTab] = useState<"DETAILS" | "CHAT">(
    "CHAT"
  );

  const onTaskChatClick = useCallback((taskId: string) => {
    taskChatSlider.open();
    setTaskId(taskId);
    setDefaultTaskTab("CHAT");
  }, []);

  const onTaskSliderClose = () => {
    taskChatSlider.close();
    setTaskId(null);
  };

  const openTaskDetails = (taskId: string) => {
    taskChatSlider.open();
    setTaskId(taskId);
    setDefaultTaskTab("DETAILS");
  };

  const tableData: (EntityTask & { subRows?: EntityTask[] })[] = useMemo(() => {
    const templateMap = new Map<string, Array<any>>();

    taskList.forEach((task: any) => {
      if (!templateMap.get(task?.template?.uuid)) {
        templateMap.set(task?.template?.uuid, []);
      }

      templateMap.get(task?.template?.uuid)?.push(task);
    });

    const subTaskTemplateMap = taskList.reduce((acc, task) => {
      if (
        task.parent_task_template_id &&
        templateMap.get(task.parent_task_template_id)?.length
      ) {
        return {
          ...acc,
          [task.parent_task_template_id]: [
            ...(acc[task.parent_task_template_id] || []),
            task,
          ],
        };
      }

      return acc;
    }, {} as Record<string, EntityTask[]>);

    const tasksAndTheirSubtasks = taskList.map((parent) => {
      const subTasks = subTaskTemplateMap[parent?.template?.uuid]?.map(
        (task, i, arr) => {
          const isLast = i === arr.length - 1;

          return {
            ...task,
            isSubTask: true,
            isLast,
          };
        }
      );

      return {
        ...parent,
        subRows: subTasks || [],
        toggleAllRowsExpanded: true,
      };
    });

    const subTaskIds = tasksAndTheirSubtasks.flatMap((t) =>
      t.subRows.map((st) => st.uuid)
    );

    return tasksAndTheirSubtasks.filter(
      (task) => !subTaskIds.includes(task.uuid)
    );
  }, [taskList]);

  const columnHelper = createColumnHelper<
    EntityTask & { isLast?: boolean; subRows?: EntityTask[] }
  >();

  const columns = useMemo(() => {
    const columnsBasedOnStatus =
      selectedTab === "COMPLETED"
        ? [
            columnHelper.accessor("bookkeeping_task_start_date", {
              header: () => (
                <ToolTip text="Estimated date when the task is picked up.">
                  <span className="t-inline-flex t-gap-1 t-items-center">
                    <span>Pickup ETA</span>
                    <span className="t-flex">
                      <InfoFilledSmall color="currentColor" />
                    </span>
                  </span>
                </ToolTip>
              ),
              size: 10,
              cell: (info) => (
                <span>
                  {info.getValue() ? formatDate(info.getValue()) : "-"}
                </span>
              ),
            }),
            columnHelper.accessor("completed_on", {
              header: () => (
                <ToolTip text="Date when the task is marked as closed.">
                  <span className="t-inline-flex t-gap-1 t-items-center">
                    <span>Completed On</span>
                    <span className="t-flex">
                      <InfoFilledSmall color="currentColor" />
                    </span>
                  </span>
                </ToolTip>
              ),
              size: 10,
              cell: (info) => (
                <span className="t-text-body-sm t-text-text-30">
                  {info.getValue() ? formatDate(info.getValue()) : "-"}
                </span>
              ),
            }),
          ]
        : [
            columnHelper.accessor("estimated_time_of_delivery", {
              cell: ({ row }) => (
                <EstimdatedTimeColumn
                  taskData={row.original as unknown as Task}
                  isCustomTrigger={true}
                />
              ),
              header: () => (
                <ToolTip text="Estimated date by which the bookkeeper expects to complete the task.">
                  <span className="t-inline-flex t-gap-1 t-items-center">
                    <span>Completion ETD</span>
                    <span className="t-flex">
                      <InfoFilledSmall color="currentColor" />
                    </span>
                  </span>
                </ToolTip>
              ),
              size: 10,
            }),
            columnHelper.accessor("bookkeeping_task_start_date", {
              header: () => (
                <ToolTip text="Estimated date when the task is picked up.">
                  <span className="t-inline-flex t-gap-1 t-items-center">
                    <span>Pickup ETA</span>
                    <span className="t-flex">
                      <InfoFilledSmall color="currentColor" />
                    </span>
                  </span>
                </ToolTip>
              ),
              size: 10,
              cell: ({ row }) => {
                return (
                  <BookkeepingStartedOn
                    taskId={row.original.uuid}
                    startedOn={row.original.bookkeeping_task_start_date}
                  />
                );
              },
            }),
          ];

    return [
      columnHelper.accessor("name", {
        header: "SERVICE",
        size: 10,
        cell: ({ row }) => {
          const { name, from_date, to_date, accounting_method, is_archived } =
            row.original;

          const {
            original: { uuid, isLast },
          } = row;

          // Checks the subrow id to see if there is only one subtask
          // Row id would be something like 2.0
          const hasSingleSubtask = row.id.split(".")[1] === "0";

          return (
            <div
              className="t-relative t-flex t-gap-1"
              style={{
                paddingLeft: `${row.depth}rem`,
              }}
            >
              <span>
                {row.depth > 0 ? (
                  <>
                    <img src={ArrowBend} alt="Subtask" />
                    <img
                      className={classNames("t-absolute t-left-[16.8px] ", {
                        "t-top-[-64px]": isLast,
                        "-t-top-[15px]": !isLast,
                        "t-hidden": row?.original?.isLast && hasSingleSubtask,
                      })}
                      src={VerticalLine}
                      alt="Subtask"
                    />
                  </>
                ) : null}
              </span>
              <div>
                <span className="t-flex t-items-center t-gap-2 t-pr-3 t-text-subtitle-sm t-font-medium t-text-text-100 t-group">
                  <span
                    className="t-line-clamp-2 hover:t-underline t-text-purple"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <ConditionalLink
                      to={`/filings/${uuid}`}
                      target="_blank"
                      className="t-text-purple hover:t-text-purple"
                    >
                      {name} &nbsp;
                      <img
                        src={Redirect}
                        alt="redirect"
                        className="group-hover:!t-visible t-invisible t-h-4 t-w-4"
                      />
                    </ConditionalLink>
                  </span>
                  {is_archived && (
                    <Tag icon={false} size="small" tagType="gray">
                      A
                    </Tag>
                  )}
                </span>
                <div className="t-flex t-items-center t-gap-1">
                  {from_date && to_date && (
                    <div className="t-text-body-sm t-text-text-30">
                      <Period start_date={from_date} end_date={to_date} />
                    </div>
                  )}

                  {from_date && to_date && accounting_method && (
                    <img src={VerticalSeparator} alt="Vertical Separator" />
                  )}

                  {accounting_method && (
                    <span className="t-text-body-sm t-text-text-30">
                      {capitalize(accounting_method)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor("tags", {
        cell: ({ row }) => {
          return (
            <div className="t-flex t-justify-center t-w-full">
              <FilingTags
                taskId={row.original.uuid}
                currentTags={row.original.tags}
                tags={tags}
              />
            </div>
          );
        },
        header: "TASK TAGS",
        size: 10,
      }),
      columnHelper.accessor("status", {
        header: "Status",
        size: 15,
        cell: ({ row }) => (
          <div className="t-flex t-justify-center t-w-full">
            {row.original.books_global_status ? (
              <BooksGlobalStatus
                taskId={row.original.uuid}
                status={row.original.books_global_status}
              />
            ) : (
              <TaskStatus
                status={row.original.status || "COMPLETED"}
                state={row?.original?.state as unknown as null}
                statusType={row?.original?.state_details?.type}
              />
            )}
          </div>
        ),
      }),

      columnHelper.accessor("deadline", {
        header: "Deadline",
        size: 10,
        cell: (info) => {
          return (
            <div className="t-flex t-justify-center t-w-full">
              <TaskDeadlineUpdate
                taskId={info.row.original.uuid}
                deadline={info.row.original.deadline}
              />
            </div>
          );
        },
      }),

      ...columnsBasedOnStatus,

      columnHelper.accessor("assigned_to.uuid", {
        cell: ({ row }) => (
          <PreparerColumn taskData={row.original as unknown as Task} />
        ),
        header: "BOOKKEEPER",
        size: 12,
      }),

      columnHelper.display({
        cell: (info) => (
          <TaskNotes
            taskId={info.row.original.uuid}
            entityId={entityId}
            groupId={group.uuid}
            currentNoteLabel="Notes for this Task"
            allNotesCount={info.row.original.notes_count ?? 0}
          />
        ),
        header: "Notes",
        size: 5,
      }),
      columnHelper.accessor("channel", {
        header: "",
        cell: memo((info: CellContext<EntityTask, string>) => (
          //@ts-ignore
          <ChatSlider
            {...info}
            channelUnreadMap={channelUnreadMap}
            openTaskChat={onTaskChatClick}
          />
        )),
        size: 5,
      }),
    ];
  }, [selectedTab, tags]);

  const table = useReactTable({
    columns,
    autoResetExpanded: false,
    data: isLoading ? taskDummyData : tableData,
    getSubRows: (row) => row.subRows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    state: {
      expanded: true,
    },
    enableExpanding: true,
    defaultColumn: {
      size: 10,
      minSize: 5,
    },
  });

  const setRowStyles = (
    row: Row<
      EntityTask & {
        isLast?: boolean | undefined;
        isSubTask?: boolean | undefined;
      }
    >
  ) => {
    if (row.original?.isLast) {
      return "";
    }

    if (row.subRows.length || row.original?.isSubTask) {
      return "t-border-b-0";
    }
    return "";
  };

  return (
    <div className="t-h-full t-w-full t-overflow-x-auto t-border t-border-solid t-border-neutral-10 t-rounded-md t-p-2">
      <Table.Container isLoading={isLoading}>
        <Table.Content className="t-w-full t-overflow-auto">
          <Table.Head
            className={classNames(
              "t-inline-table t-table-fixed t-w-full t-bg-neutral-10 !t-border-b",
              {
                "!t-rounded-t-none": false,
              }
            )}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.HeadRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Table.HeadCell
                    key={header.id}
                    style={{ width: `${header.getSize()}%` }}
                    className="t-py-4 t-px-3 t-uppercase t-text-subtext-sm t-bg-surface-lighter-grey t-items-center t-text-center"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Table.HeadCell>
                ))}
              </Table.HeadRow>
            ))}
          </Table.Head>
          <Table.Body className="all:unset t-inline-table t-table-fixed t-w-full">
            {table.getRowModel().rows.map((row) => {
              const {
                original: { uuid },
              } = row;

              return (
                <Table.Row
                  key={row.id}
                  onClick={(e) => openTaskDetails(uuid)}
                  className={classNames(
                    `t-cursor-pointer t-items-center t-justify-center t-gap-6 t-border-b t-border-r-0 t-border-t-0 t-border-solid t-border-i-neutral-10 t-border-l-surface-transparent hover:t-bg-surface-lighter-grey t-h-max ${setRowStyles(
                      row
                    )}`,
                    {
                      "t-bg-purple-0": row.original.uuid === taskId,
                    }
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Table.Cell
                      key={cell.id}
                      style={{ width: `${cell.column.getSize()}%` }}
                      className="t-align-middle t-px-3 t-py-2"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Table.Cell>
                  ))}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Content>
      </Table.Container>
      <BookkeepingTaskSlider
        taskId={taskId}
        isOpen={taskChatSlider.isOpen}
        onClose={onTaskSliderClose}
        defaultTab={defaultTaskTab}
        group={group}
        bookkeepingSoftware={bookkeepingSoftware}
        connectionDetails={connectionDetails}
      />
    </div>
  );
};
